// ─── EmptyState — los 4 vacíos de un bloque del diseño (I-244) ────────────────
// Fuente: `handoff_claude_ingresos_vestido/casa-ingresos-vestido.jsx:209-236` (`VacioBloque`) y README §7.
//   arranque  (nada todavía)        borde punteado `line`, sin relleno, icono sobre `chip`
//   bueno     (nada MALO)           `accentSoft` verde, título en `accent`
//   cargando  (esqueleto)           filas de huesos + pie «Cargando…»
//   caido     (falló)               `dangerSoft`, título en `danger`, «Volver a intentar»
// Medidas: relleno 34/22, radio 14, gap 8 · icono 42 radio 12 (glifo 19) · título 15/700 · texto 12.5 `fg2` ancho 460, interlínea 1.55.
// Una sola forma: se viste siempre con el tema (`useTema()`, el claro fuera de la casa).
import React from 'react';
import { InboxIcon, Check, AlertTriangle } from 'lucide-react';
import { PASO, useTema, type CasaTema } from '@components/M02/casaTema';
import { Button } from './Button';
import { TONOS_ALERTA } from './Alerta';
import { Hueso as HuesoBase } from './Hueso';
import type { ContenedorTema } from './contenedorTema';

export type TipoVacio = 'arranque' | 'bueno' | 'cargando' | 'caido';

interface EmptyStateProps {
  /** El tipo del diseño. Sin él: `arranque`, que es el «no hay nada» de siempre. */
  tipo?: TipoVacio;
  icon?: React.ReactNode;
  /** BUG-2364: sin ícono en absoluto — la caja entera desaparece (`TableroVacio` nunca tuvo). */
  sinIcono?: boolean;
  title: string;
  description?: React.ReactNode;
  /** Ancho del párrafo de `description`, en px. Default 460 (el del diseño). */
  descriptionMaxWidth?: number;
  action?: {
    label: string;
    onClick: () => void;
    /** BUG-2387: la acción se ve pero no se puede usar (p. ej. proyecto cerrado) con el motivo en `disabledReason`. */
    disabled?: boolean;
    disabledReason?: string;
    icon?: React.ReactNode;
  };
  /** Sólo `caido`: el «Volver a intentar». */
  onReintentar?: () => void;
  /** Sólo `cargando`: cuántas filas de esqueleto (4 por defecto). */
  filas?: number;
  /** Alias: el tema de la casa, o el contrato chico `ContenedorTema` de las ventanas. Sin él: `useTema()`. */
  tema?: ContenedorTema | CasaTema;
}

/** El contrato chico de las ventanas → los colores que el vacío necesita; lo que no trae (acento, rojo) sale del tema de la casa. */
function vestir(base: CasaTema, tema?: ContenedorTema | CasaTema): CasaTema {
  if (!tema) return base;
  if ('dark' in tema) return tema;
  return { ...base, chip: tema.chip, line: tema.line, panelSoft: tema.panelSoft, panelSolid: tema.panelSolid, fg: tema.fg, fg2: tema.fg3, fg3: tema.fgTenue };
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  tipo = 'arranque', icon, sinIcono, title, description, descriptionMaxWidth, action, onReintentar, filas = 4, tema,
}) => {
  const delTema = useTema();
  const Hueso = (p: { w: number | string; h?: number; r?: number }) => <HuesoBase {...p} tema={T} />;
  const T = vestir(delTema, tema);

  if (tipo === 'cargando') {
    return (
      <div data-testid="vacio-cargando" style={{ borderRadius: 14, border: `1px solid ${T.line}`, background: T.panel, overflow: 'hidden' }}>
        {Array.from({ length: filas }).map((_, i) => (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: 'minmax(0,1.6fr) 104px 150px 120px', gap: 12, alignItems: 'center',
            padding: '15px 16px', borderTop: i ? `1px solid ${T.lineSoft}` : 0,
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}><Hueso w="52%" h={11} /><Hueso w="78%" h={9} /></div>
            <Hueso w={84} h={13} /><Hueso w={120} h={13} /><Hueso w={96} h={28} r={9} />
          </div>
        ))}
        <div style={{ padding: '10px 16px', borderTop: `1px solid ${T.lineSoft}`, fontSize: PASO.s11_5, color: T.fg3 }}>{title || 'Cargando…'}</div>
      </div>
    );
  }

  const bueno = tipo === 'bueno';
  const caido = tipo === 'caido';
  const marcado = bueno || caido;
  const to = TONOS_ALERTA(T)[bueno ? 'ok' : caido ? 'error' : 'neutro'];
  const Glifo = bueno ? Check : caido ? AlertTriangle : null;

  return (
    <div data-testid={`vacio-${tipo}`} style={{
      padding: '34px 22px', borderRadius: 14,
      border: `1px ${marcado ? 'solid' : 'dashed'} ${marcado ? to.bd : T.line}`,
      background: marcado ? to.bg : 'transparent',
      display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 8,
    }}>
      {!sinIcono && (
        <div data-testid="vacio-icono" style={{
          width: 42, height: 42, borderRadius: 12, background: marcado ? T.panelSolid : T.chip,
          border: `1px solid ${marcado ? to.bd : 'transparent'}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: marcado ? to.fg : T.fg3,
        }}>
          {icon ?? (Glifo ? <Glifo size={19} /> : <InboxIcon size={19} />)}
        </div>
      )}
      <div style={{ fontSize: PASO.s15, fontWeight: 700, color: marcado ? to.fg : T.fg }}>{title}</div>
      {description && (
        <div style={{ fontSize: PASO.s12_5, color: T.fg2, maxWidth: descriptionMaxWidth ?? 460, lineHeight: 1.55 }}>{description}</div>
      )}
      {(caido || action) && (
        <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
          {caido && <Button tema={T} variant="primary" size="sm" onClick={onReintentar}>Volver a intentar</Button>}
          {action && (
            <Button
              tema={T}
              variant={caido ? 'secondary' : 'primary'}
              size="sm"
              icon={action.icon}
              onClick={action.onClick}
              razon={action.disabled ? (action.disabledReason ?? 'No se puede hacer ahora') : undefined}
            >
              {action.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default EmptyState;
